import React, { useEffect, useState, useCallback } from 'react';
import { Download } from 'lucide-react';
import api from '../../services/api';
import alertService from '../../utils/alert';

const err = (e, fallback) => e.response?.data?.error || fallback;

// Opt-in / opt-out records: who replied STOP, who opted in, and the rule for who may be messaged first.
export default function WhatsAppConsentPanel() {
  const [data, setData] = useState({ rows: [], total: 0, requireOptIn: false });
  const [status, setStatus] = useState('opted_out');
  const [phone, setPhone] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async (s) => {
    try {
      const r = await api.get('/meta/consent', { params: { status: s, limit: 10 } });
      setData({ rows: r.data.data || [], total: r.data.total || 0, requireOptIn: !!r.data.requireOptIn });
    } catch (e) { alertService.error('Error', err(e, 'Could not load opt-in records')); }
  }, []);
  useEffect(() => { load(status); }, [load, status]);

  const toggleRequire = async () => {
    try { await api.put('/meta/consent/settings', { requireOptIn: !data.requireOptIn }); load(status); } catch (e) { alertService.error('Error', err(e, 'Only an admin can change this')); }
  };
  const record = async (next) => {
    setBusy(true);
    try { await api.put('/meta/consent', { phone, status: next }); setPhone(''); load(status); } catch (e) { alertService.error('Error', err(e, 'Could not save')); } finally { setBusy(false); }
  };
  const download = async () => {
    try {
      const r = await api.get('/meta/consent/export', { responseType: 'blob' });
      const a = document.createElement('a');
      a.href = URL.createObjectURL(r.data); a.download = 'whatsapp-consent.csv'; a.click(); URL.revokeObjectURL(a.href);
    } catch (e) { alertService.error('Error', err(e, 'Could not export')); }
  };

  return (
    <div className="sf-card">
      <div className="sf-card-header flex items-center justify-between">
        <h3 className="sf-card-title">Opt-in and opt-out</h3>
        <button onClick={download} className="flex items-center gap-1 text-[12px] font-semibold text-[#0176D3]"><Download size={13} /> Export CSV</button>
      </div>
      <div className="sf-card-body space-y-3">
        <p className="text-[12px] text-[#706E6B]">Customers who reply STOP are never messaged again until they reply START. Customers who write to you count as opted in.</p>
        <label className="flex items-start gap-2 text-[13px] text-[#080707]">
          <input type="checkbox" checked={data.requireOptIn} onChange={toggleRequire} className="mt-1" />
          <span>Only send template messages to numbers that opted in<br /><span className="text-[11px] text-[#706E6B]">Off: templates go to everyone who has not opted out.</span></span>
        </label>
        <div className="flex gap-2 text-[12px]">
          {[['opted_out', 'Opted out'], ['opted_in', 'Opted in']].map(([k, label]) => (
            <button key={k} onClick={() => setStatus(k)} className={`px-3 py-1 rounded-full border ${status === k ? 'bg-[#0176D3] text-white border-[#0176D3]' : 'border-[#D8DDE6] text-[#706E6B]'}`}>{label}{status === k ? ` (${data.total})` : ''}</button>
          ))}
        </div>
        <ul className="text-[12px] divide-y divide-[#F3F2F2]">
          {data.rows.map((r) => (
            <li key={r.phone} className="py-1.5 flex justify-between"><span className="font-mono">{r.phone}</span><span className="text-[#706E6B]">{new Date(r.status === 'opted_out' ? r.optedOutAt : r.optedInAt).toLocaleDateString()} · {r.status === 'opted_out' ? r.optedOutSource : r.optedInSource}</span></li>
          ))}
          {!data.rows.length && <li className="py-2 text-[#706E6B]">None yet</li>}
        </ul>
        <div className="flex gap-2">
          <input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="Number, e.g. 919876543210" className="flex-1 border border-[#D8DDE6] rounded-[4px] px-3 py-1.5 text-[12px]" />
          <button disabled={busy || !phone.trim()} onClick={() => record('opted_in')} className="px-3 text-[12px] font-semibold text-[#2E844A] disabled:opacity-50">Opt in</button>
          <button disabled={busy || !phone.trim()} onClick={() => record('opted_out')} className="px-3 text-[12px] font-semibold text-[#BA0517] disabled:opacity-50">Opt out</button>
        </div>
      </div>
    </div>
  );
}
